import { exercises as rawExercises, sources as rawSources } from "../../questions/index.js";
import { curationOverrides, type CurationOverride } from "./curation";
import { claimLab } from "./claim-lab";
import { flashcards } from "./flashcards";
import { hagenStellvertretung } from "./hagen-stellvertretung";
import { mcVariants } from "./mcVariants";
import { clozes } from "./clozes";
import { orderings } from "./orderings";
import { hagenBgbAtVertiefung } from "./hagen-bgb-at-vertiefung";
import { hagenSchuldrechtAt } from "./hagen-schuldrecht-at";
import { hagenSachenrecht } from "./hagen-sachenrecht";
import { ausbildungVerletzung } from "./deutsche-ausbildung-verletzung";
import { eqeEinspruchBeschwerde } from "./eqe-einspruch-beschwerde";
import { ausbildungMarke } from "./deutsche-ausbildung-marke";
import { eqeFristenSchutzbereich } from "./eqe-fristen-schutzbereich";
import { ausbildungVollabdeckung } from "./deutsche-ausbildung-vollabdeckung";
import { eqeVerfahrenPctDrafting } from "./eqe-verfahren-pct-drafting";
import { ausbildungPatg3 } from "./deutsche-ausbildung-patg3";
import { ausbildungGebrauchsmuster } from "./deutsche-ausbildung-gebrauchsmuster";
import { ausbildungBerufsrecht } from "./deutsche-ausbildung-berufsrecht";
import { ausbildungDesign } from "./deutsche-ausbildung-design";
import { ausbildungArbnErf } from "./deutsche-ausbildung-arbnerf";
import { epcGrundlagen } from "./epc-grundlagen";
import { ausbildungUnionsmarke } from "./deutsche-ausbildung-unionsmarke";
import { eqePctVertieft } from "./eqe-pct-vertieft";
import { eqePaperF } from "./eqe-paperf";
import { ausbildungSonstiges } from "./deutsche-ausbildung-sonstiges";
import type { ExerciseKind, LearningExercise, LearningPath, LearningPathId, ReviewStatus, SourceRef, ValidationIssue } from "../types";

const ALLOWED_REVIEW_STATUS: ReviewStatus[] = ["draft", "source-checked", "reviewed", "beta-ready", "rejected"];

// reviewStatus is honest workflow state: imported questions are unreviewed (draft) until
// question-reviewer promotes them. An explicit, valid status on the raw record is preserved.
function reviewStatusFor(raw: Record<string, unknown>): ReviewStatus {
  const candidate = String(raw.reviewStatus ?? "");
  return (ALLOWED_REVIEW_STATUS as string[]).includes(candidate) ? (candidate as ReviewStatus) : "draft";
}

// Deterministic id so SRS progress (state.cards[id]) survives reloads even when a raw
// question lacks an id. Same content -> same id (no random UUID).
function stableIdFor(raw: Record<string, unknown>): string {
  if (raw.id != null && String(raw.id).length > 0) return String(raw.id);
  const basis = `${raw.mode ?? ""}|${raw.topic ?? ""}|${raw.title ?? ""}|${raw.prompt ?? ""}`;
  let hash = 0;
  for (let i = 0; i < basis.length; i++) {
    hash = (Math.imul(hash, 31) + basis.charCodeAt(i)) | 0;
  }
  return `gen-${(hash >>> 0).toString(36)}`;
}

const levelLabels = ["Bekannt", "Abrufbar", "Anwendbar", "Pruefungsstabil", "Unter Zeitdruck stabil"];

const levelNameToNumber: Record<string, number> = {
  bekannt: 1,
  abrufbar: 2,
  anwendbar: 3,
  "pruefungsstabil": 4,
  "prüfungsstabil": 4,
  "unter zeitdruck stabil": 5,
};

export const learningPaths: LearningPath[] = [
  {
    id: "deutsche-ausbildung",
    label: "Deutsche Ausbildung",
    description: "PatG, Verfahren, Verletzung, Marken und Berufsrecht.",
    accent: "#71d5e8",
  },
  {
    id: "epu-eqe",
    label: "EPU / EQE",
    description: "EPC, PCT, Guidelines und pruefungsnahe Claim-Arbeit.",
    accent: "#9ee493",
  },
  {
    id: "hagen-jura",
    label: "Hagen Jura",
    description: "BGB AT, Klausurtechnik und Wiederholungssets.",
    accent: "#f5c86b",
  },
];

export const sources: Record<string, SourceRef> = Object.fromEntries(
  Object.entries(rawSources).map(([id, source]) => [
    id,
    {
      id,
      label: String(source?.label ?? id),
      url: typeof source?.url === "string" ? source.url : undefined,
    },
  ])
);

function normalizeLevel(level: unknown): { label: string; value: number } {
  if (typeof level === "number") {
    const value = Math.max(1, Math.min(5, level));
    return { label: levelLabels[value - 1] ?? String(level), value };
  }

  const label = String(level || "Abrufbar");
  const value = levelNameToNumber[label.toLowerCase()] ?? 2;
  return { label, value };
}

function normalizeKind(kind: unknown, answers: number[], options: string[]): ExerciseKind {
  if (kind === "single" || kind === "multi" || kind === "allnone") return kind;
  if (answers.length === 0 || answers.length === options.length) return "allnone";
  return answers.length > 1 ? "multi" : "single";
}

function pathFor(raw: Record<string, unknown>): LearningPathId {
  const haystack = `${raw.mode ?? ""} ${raw.id ?? ""} ${raw.topic ?? ""}`.toLowerCase();
  if (haystack.includes("hagen") || haystack.includes("hj-") || haystack.includes("bgb")) return "hagen-jura";
  if (haystack.includes("epc") || haystack.includes("epü") || haystack.includes("epu") || haystack.includes("pct")) {
    return "epu-eqe";
  }
  if (haystack.includes("ausbildung") || haystack.includes("patg") || haystack.includes("da-")) {
    return "deutsche-ausbildung";
  }
  return "foundation";
}

function sourceIdsFor(raw: Record<string, unknown>): string[] {
  if (Array.isArray(raw.sources)) return raw.sources.map(String).filter(Boolean);
  if (typeof raw.source === "string" && raw.source) return [raw.source];
  return [];
}

function optionsFor(raw: Record<string, unknown>): { options: string[]; answers: number[] } {
  const rawOptions = Array.isArray(raw.options) ? raw.options : [];

  if (rawOptions.length && typeof rawOptions[0] === "object") {
    const optionObjects = rawOptions as Array<{ text?: unknown; correct?: unknown }>;
    return {
      options: optionObjects.map((option) => String(option.text ?? "")),
      answers: optionObjects.flatMap((option, index) => (option.correct ? [index] : [])),
    };
  }

  const options = rawOptions.map(String);
  const rawAnswer = raw.answer;
  const answers = Array.isArray(rawAnswer)
    ? rawAnswer.map(Number).filter((value) => Number.isInteger(value))
    : Number.isInteger(Number(rawAnswer))
      ? [Number(rawAnswer)]
      : [];

  return { options, answers };
}

function skillTagsFor(raw: Record<string, unknown>): string[] {
  const text = `${raw.id ?? ""} ${raw.topic ?? ""} ${raw.title ?? ""} ${raw.prompt ?? ""}`.toLowerCase();
  const tags: string[] = [];
  if (text.includes("neuheit") || text.includes("art. 54")) tags.push("neuheit");
  if (text.includes("erfinder") || text.includes("art. 56")) tags.push("erfinderische-taetigkeit");
  if (text.includes("anspruch") || text.includes("claim")) tags.push("claim-analysis");
  if (text.includes("einspruch")) tags.push("einspruch");
  if (text.includes("frist") || text.includes("priorit")) tags.push("fristen-prioritaet");
  if (text.includes("pct")) tags.push("pct");
  if (text.includes("bgb") || text.includes("vertrag")) tags.push("bgb-at");
  if (text.includes("verletzung")) tags.push("verletzung");
  return tags.length ? tags : ["grundlagen"];
}

export function mapRawExercise(
  raw: Record<string, unknown>,
  overrides: Record<string, CurationOverride> = curationOverrides
): LearningExercise {
  const { options, answers } = optionsFor(raw);
  const sourceIds = sourceIdsFor(raw);
  const id = stableIdFor(raw);
  const override = overrides[id] ?? {};
  const levelInfo = override.level != null ? normalizeLevel(override.level) : normalizeLevel(raw.level);

  const exercise: LearningExercise = {
    id,
    pathId: override.pathId ?? pathFor(raw),
    mode: String(raw.mode ?? "foundation"),
    kind: normalizeKind(raw.kind, answers, options),
    topic: String(raw.topic ?? "Unsortiert"),
    title: String(raw.title ?? "Unbenannte Frage"),
    prompt: String(raw.prompt ?? ""),
    options,
    answers,
    explanation: String(raw.explanation ?? ""),
    sourceIds,
    sources: sourceIds.map((sourceId) => sources[sourceId]).filter(Boolean),
    level: levelInfo.label,
    levelNumber: levelInfo.value,
    errorCategory: override.errorCategory ?? String(raw.error ?? "Noch nicht kategorisiert"),
    skillTags: override.skillTags ?? skillTagsFor(raw),
    reviewStatus: override.reviewStatus ?? reviewStatusFor(raw),
  };

  // Adaptive Distraktor-Varianten anhaengen, falls fuer diese id vorhanden (Overlay).
  const variant = mcVariants[id];
  if (variant) {
    exercise.correctTexts = variant.correctTexts;
    exercise.distractorsByDifficulty = variant.distractorsByDifficulty;
  }
  return exercise;
}

export const exercises: LearningExercise[] = [
  ...(rawExercises as Array<Record<string, unknown>>).map((raw) => mapRawExercise(raw)),
  ...flashcards,
  ...orderings,
  ...clozes,
  ...hagenStellvertretung,
  ...hagenBgbAtVertiefung,
  ...hagenSchuldrechtAt,
  ...hagenSachenrecht,
  ...ausbildungVerletzung,
  ...eqeEinspruchBeschwerde,
  ...ausbildungMarke,
  ...eqeFristenSchutzbereich,
  ...ausbildungVollabdeckung,
  ...eqeVerfahrenPctDrafting,
  ...ausbildungPatg3,
  ...ausbildungGebrauchsmuster,
  ...ausbildungBerufsrecht,
  ...ausbildungDesign,
  ...ausbildungArbnErf,
  ...epcGrundlagen,
  ...ausbildungUnionsmarke,
  ...eqePctVertieft,
  ...eqePaperF,
  ...ausbildungSonstiges,
  ...claimLab,
];

export function validateQuestionBank(questionBank: LearningExercise[] = exercises): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const ids = new Set<string>();

  questionBank.forEach((exercise) => {
    if (ids.has(exercise.id)) {
      issues.push({ id: exercise.id, severity: "error", message: "ID ist doppelt vergeben." });
    }
    ids.add(exercise.id);

    if (exercise.kind === "flashcard") {
      // Karteikarten: Vorderseite (prompt) + Rueckseite (explanation), keine Optionen.
      if (!exercise.explanation) {
        issues.push({ id: exercise.id, severity: "error", message: "Karteikarte ohne Rueckseite (explanation)." });
      }
      if (exercise.sourceIds.length === 0 || exercise.sources.length === 0) {
        issues.push({ id: exercise.id, severity: "warning", message: "Karteikarte ohne Quelle." });
      }
      return;
    }

    if (exercise.distractorsByDifficulty && exercise.correctTexts) {
      // Varianten-Item: keine festen options; richtige Antwort + 3 Distraktoren je vorhandener Stufe.
      if (exercise.correctTexts.length === 0) {
        issues.push({ id: exercise.id, severity: "error", message: "Varianten-Frage ohne richtige Antwort." });
      }
      for (const [level, list] of Object.entries(exercise.distractorsByDifficulty)) {
        if ((list?.length ?? 0) !== 3) {
          issues.push({ id: exercise.id, severity: "error", message: `Varianten-Stufe ${level} braucht genau 3 Distraktoren.` });
        }
      }
      if (exercise.sourceIds.length === 0 || exercise.sources.length === 0) {
        issues.push({ id: exercise.id, severity: "error", message: "Quelle fehlt oder ist nicht im Quellenmanifest vorhanden." });
      }
      return;
    }

    if (exercise.kind === "ordering") {
      if ((exercise.orderItems?.length ?? 0) < 2) {
        issues.push({ id: exercise.id, severity: "error", message: "Sortieraufgabe braucht mindestens 2 Items." });
      }
      if (exercise.sourceIds.length === 0 || exercise.sources.length === 0) {
        issues.push({ id: exercise.id, severity: "error", message: "Quelle fehlt oder ist nicht im Quellenmanifest vorhanden." });
      }
      return;
    }

    if (exercise.kind === "cloze") {
      if (!exercise.prompt.includes("___")) {
        issues.push({ id: exercise.id, severity: "error", message: "Cloze ohne Luecke (___)." });
      }
      if (exercise.options.length < 2) {
        issues.push({ id: exercise.id, severity: "error", message: "Cloze braucht mindestens 2 Auswahloptionen." });
      }
      if (exercise.answers.length !== 1) {
        issues.push({ id: exercise.id, severity: "error", message: "Cloze braucht genau eine richtige Antwort." });
      }
      if (exercise.sourceIds.length === 0 || exercise.sources.length === 0) {
        issues.push({ id: exercise.id, severity: "error", message: "Quelle fehlt oder ist nicht im Quellenmanifest vorhanden." });
      }
      return;
    }

    if (exercise.options.length !== 4) {
      issues.push({ id: exercise.id, severity: "error", message: "Frage hat nicht genau vier Antwortoptionen." });
    }
    if (exercise.kind === "single" && exercise.answers.length !== 1) {
      issues.push({ id: exercise.id, severity: "error", message: "Single-Frage braucht genau eine richtige Antwort." });
    }
    if (exercise.kind === "multi" && (exercise.answers.length < 2 || exercise.answers.length > 3)) {
      issues.push({ id: exercise.id, severity: "warning", message: "Multi-Frage sollte zwei bis drei richtige Antworten haben." });
    }
    if (exercise.sourceIds.length === 0 || exercise.sources.length === 0) {
      issues.push({ id: exercise.id, severity: "error", message: "Quelle fehlt oder ist nicht im Quellenmanifest vorhanden." });
    }
    // reviewStatus is workflow state, not a structural property: it is reported via
    // betaReadyCount / countByReviewStatus, not flagged as a validation error here.
  });

  return issues;
}
